/**
 * First-run onboarding: step model.
 *
 * Mirrors the Swift flow (`apps/swift/Sources/Caliu/Views/Onboarding/`):
 * welcome → magic (live markdown demo) → theme → backup → pro (Swift adds a
 * density step web has no equivalent for). Completion is persisted per browser under
 * `caliu-completed-onboarding`, the same key name the native apps use in
 * UserDefaults.
 */

export const ONBOARDING_COMPLETED_KEY = 'caliu-completed-onboarding'

// 'backup' is "Where your notes live": the persistence story, and on the
// account-less front door the web consent moment (backup defaults ON because
// browsers can evict site data — plan Part 2, step 5).
export const ONBOARDING_STEPS = ['welcome', 'magic', 'theme', 'backup', 'pro'] as const
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number]

export function nextOnboardingStep(step: OnboardingStep): OnboardingStep | null {
  const idx = ONBOARDING_STEPS.indexOf(step)
  return ONBOARDING_STEPS[idx + 1] ?? null
}

export function previousOnboardingStep(step: OnboardingStep): OnboardingStep | null {
  const idx = ONBOARDING_STEPS.indexOf(step)
  return idx > 0 ? ONBOARDING_STEPS[idx - 1] : null
}

export function isOnboardingCompleted(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_COMPLETED_KEY) === 'true'
  } catch {
    // Storage unavailable (private mode edge cases): don't loop onboarding.
    return true
  }
}

export function markOnboardingCompleted(): void {
  try {
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true')
  } catch {
    // Expected in storage-restricted contexts; nothing to report.
  }
}

/**
 * The welcome intro plays once per browser: later tours (after a sign-out,
 * say) open on the finished welcome step. A replay from Settings is the user
 * asking to see it, so it plays again. Swift `WelcomeIntro.shouldPlay`,
 * Android `WelcomeIntro.shouldPlay`. The key survives the sign-out wipe
 * (`wipeLocalStorage` in `store/auth.ts`).
 */
export const WELCOME_INTRO_PLAYED_KEY = 'caliu-welcome-intro-played'

export function shouldPlayWelcomeIntro(alreadyPlayed: boolean, isReplay: boolean): boolean {
  return isReplay || !alreadyPlayed
}

export function hasPlayedWelcomeIntro(): boolean {
  try {
    return localStorage.getItem(WELCOME_INTRO_PLAYED_KEY) === 'true'
  } catch {
    return false
  }
}

export function markWelcomeIntroPlayed(): void {
  try {
    localStorage.setItem(WELCOME_INTRO_PLAYED_KEY, 'true')
  } catch {
    // Expected in storage-restricted contexts; it simply plays again.
  }
}

/* ── Welcome intro ("Cursor") ────────────────────────────────
 * The timeline of the welcome step's opening, in seconds from the moment the
 * step is first shown. Shared spec with Swift and Android: the logo's own
 * keyframes live in index.css (`onboarding-logo-*`); this is the part that
 * depends on the copy, so a long translation never gets cut off by the
 * subtitle or by the chrome fading in.
 */

/** The title starts typing once the plant has grown (1.5 caret + 2.8 growth + 0.5). */
export const WELCOME_INTRO_TEXT_START = 4.8
/** Seconds per typed title character. Spaces are not keystrokes. */
export const WELCOME_INTRO_TITLE_STEP = 0.055
/** Seconds per typed subtitle character, spaces included. */
export const WELCOME_INTRO_SUBTITLE_STEP = 0.05
/** How long the chrome takes to fade in once the intro has finished. */
export const WELCOME_INTRO_CHROME_FADE = 0.8

export interface WelcomeIntroChar {
  char: string
  /** When this character is typed; null for one that is not a keystroke. */
  at: number | null
}

export interface WelcomeIntroTimeline {
  title: WelcomeIntroChar[]
  subtitle: WelcomeIntroChar[]
  subtitleStart: number
  /** The intro is over: the chrome starts fading in and becomes interactive. */
  finish: number
}

/** By code point, so a surrogate pair (an emoji) is never split in two. */
function characters(text: string): string[] {
  return Array.from(text)
}

export function welcomeIntroTimeline(title: string, subtitle: string): WelcomeIntroTimeline {
  let keystrokes = 0
  const titleChars = characters(title).map((char): WelcomeIntroChar => {
    if (char.trim() === '') return { char, at: null }
    return { char, at: WELCOME_INTRO_TEXT_START + keystrokes++ * WELCOME_INTRO_TITLE_STEP }
  })
  const titleEnd = WELCOME_INTRO_TEXT_START + keystrokes * WELCOME_INTRO_TITLE_STEP

  const subtitleStart = Math.max(WELCOME_INTRO_TEXT_START + 1.25, titleEnd + 0.35)
  const subtitleChars = characters(subtitle).map(
    (char, index): WelcomeIntroChar => ({
      char,
      at: subtitleStart + index * WELCOME_INTRO_SUBTITLE_STEP,
    }),
  )
  const subtitleEnd = subtitleStart + subtitleChars.length * WELCOME_INTRO_SUBTITLE_STEP

  return {
    title: titleChars,
    subtitle: subtitleChars,
    subtitleStart,
    finish: Math.max(WELCOME_INTRO_TEXT_START + 4.4, subtitleEnd + 0.5),
  }
}
